import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowLeft, Mail, Phone, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const ForgotPasswordPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar';

  const [step, setStep] = useState(1); // 1: enter email/phone, 2: success
  const [phoneOrEmail, setPhoneOrEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phoneOrEmail.trim()) {
      toast.error(lang === 'ar' ? 'يرجى إدخال البريد الإلكتروني أو رقم الهاتف' : 'Please enter email or phone number');
      return;
    }

    setLoading(true);
    
    try {
      // Call the password reset endpoint
      const formData = new FormData();
      formData.append('email', phoneOrEmail);
      
      await api.post('/send_reset_link_email', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setStep(2);
      toast.success(lang === 'ar' ? 'تم إرسال رابط إعادة التعيين' : 'Reset link sent successfully');
    } catch (error) {
      console.error('Password reset error:', error);
      const message = error.response?.data?.message || (lang === 'ar' ? 'فشل في إرسال رابط إعادة التعيين' : 'Failed to send reset link');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="forgot-password-page"
    >
      <Card className="w-full max-w-md dark:bg-slate-800 border-0 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mb-4">
            {step === 1 ? (
              <Mail className="h-8 w-8 text-teal-600" />
            ) : (
              <CheckCircle className="h-8 w-8 text-green-600" />
            )}
          </div>
          <CardTitle className="text-2xl dark:text-white">
            {step === 1 
              ? (lang === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot Password?')
              : (lang === 'ar' ? 'تم الإرسال!' : 'Email Sent!')
            }
          </CardTitle>
          <CardDescription className="dark:text-slate-400">
            {step === 1 
              ? (lang === 'ar' 
                  ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط لإعادة تعيين كلمة المرور'
                  : 'Enter your email and we\'ll send you a link to reset your password')
              : (lang === 'ar'
                  ? 'تحقق من بريدك الإلكتروني للحصول على رابط إعادة التعيين'
                  : 'Check your email for the reset link')
            }
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          {step === 1 ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phoneOrEmail" className="dark:text-slate-300">
                  {lang === 'ar' ? 'البريد الإلكتروني أو رقم الهاتف' : 'Email or Phone Number'}
                </Label>
                <div className="relative">
                  <Mail className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} h-5 w-5 text-slate-400`} />
                  <Input
                    id="phoneOrEmail"
                    type="text"
                    value={phoneOrEmail}
                    onChange={(e) => setPhoneOrEmail(e.target.value)}
                    placeholder={lang === 'ar' ? 'example@email.com أو 0912345678' : 'example@email.com or 0912345678'}
                    className={`${isRTL ? 'pr-10' : 'pl-10'} dark:bg-slate-700 dark:text-white dark:border-slate-600`}
                    required
                    data-testid="phone-email-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    {lang === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                  </>
                ) : (
                  lang === 'ar' ? 'إرسال رابط إعادة التعيين' : 'Send Reset Link'
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <p className="text-slate-600 dark:text-slate-400">
                {lang === 'ar' 
                  ? `تم إرسال رابط إعادة التعيين إلى ${phoneOrEmail}`
                  : `A reset link has been sent to ${phoneOrEmail}`
                }
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500">
                {lang === 'ar'
                  ? 'لم تتلق البريد؟ تحقق من مجلد البريد العشوائي أو'
                  : "Didn't receive the email? Check your spam folder or"}
              </p>
              <Button 
                variant="outline" 
                onClick={() => { setStep(1); setPhoneOrEmail(''); }}
                className="dark:border-slate-600 dark:text-slate-300"
              >
                {lang === 'ar' ? 'إعادة المحاولة' : 'Try Again'}
              </Button>
            </div>
          )}

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 dark:text-teal-400"
              data-testid="back-to-login"
            >
              <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
              {lang === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
